import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	MagnifyingGlassIcon,
	XMarkIcon,
	BellAlertIcon,
	DocumentTextIcon,
	LinkIcon,
	DocumentArrowDownIcon,
} from '@heroicons/react/20/solid';
import client from '../../api/client';
import Spinner from '../Global/Spinner';
import { useAlert } from '../../context/AlertContext';

// Stage keys (stored) -> friendly labels (shown).
const STAGES = [
	{ key: 'reschedule', label: 'Reschedule' },
	{ key: 'appointment-set', label: 'Appointment Set' },
	{ key: 'sales-process-ongoing', label: 'Sales Process Ongoing' },
	{ key: 'closed-won', label: 'Closed Won' },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));

const FORECAST_CATEGORIES = [
	{ key: 'pipeline', label: 'Pipeline' },
	{ key: 'upside', label: 'Upside' },
	{ key: 'commit', label: 'Commit' },
	{ key: 'closed', label: 'Closed' },
];

// Tailwind classes per stage — used for the pill.
const STAGE_STYLES = {
	reschedule: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
	'appointment-set': 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
	'sales-process-ongoing': 'bg-violet-500/15 text-violet-300 ring-violet-500/30',
	'closed-won': 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
};

function StagePill({ stage }) {
	return (
		<span
			className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
				STAGE_STYLES[stage] || 'bg-gray-500/15 text-gray-300 ring-gray-500/30'
			}`}
		>
			{STAGE_LABEL[stage] || stage || '—'}
		</span>
	);
}

function fmtDate(d) {
	if (!d) return '—';
	return new Date(d).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
}

function fmtMoney(n) {
	if (n === null || n === undefined || n === '') return '—';
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		maximumFractionDigits: 0,
	}).format(Number(n));
}

function isPast(d) {
	return d && new Date(d).getTime() <= Date.now();
}

export default function Crm() {
	const { setAlert } = useAlert();
	const [leads, setLeads] = useState([]);
	const [stats, setStats] = useState(null);
	const [loading, setLoading] = useState(false);
	const [stageFilter, setStageFilter] = useState(''); // '' = all
	const [dueOnly, setDueOnly] = useState(false);
	const [q, setQ] = useState('');
	const [selectedId, setSelectedId] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams();
			if (stageFilter) params.set('stage', stageFilter);
			if (dueOnly) params.set('due', 'true');
			if (q.trim()) params.set('q', q.trim());
			params.set('sort', 'created');
			const [leadsRes, statsRes] = await Promise.all([
				client.get(`/crm/leads?${params.toString()}`),
				client.get('/crm/stats'),
			]);
			setLeads(leadsRes.data.leads || []);
			setStats(statsRes.data || null);
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Failed to load leads');
		} finally {
			setLoading(false);
		}
	}, [stageFilter, dueOnly, q, setAlert]);

	useEffect(() => {
		load();
	}, [load]);

	const replaceLead = (lead) => {
		setLeads((prev) => prev.map((l) => (l._id === lead._id ? lead : l)));
	};

	return (
		<div className='px-4 pb-24 sm:px-6 lg:px-8'>
			<div className='flex flex-wrap items-end justify-between gap-4'>
				<div>
					<h1 className='text-2xl font-semibold text-white'>CRM</h1>
					<p className='mt-1 text-sm text-gray-400'>
						Work your leads through the pipeline. A lightweight built-in CRM.
					</p>
				</div>
			</div>

			{/* Stats row */}
			{stats && (
				<div className='mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7'>
					<StatTile
						label='All leads'
						value={stats.total}
						active={!stageFilter && !dueOnly}
						onClick={() => {
							setStageFilter('');
							setDueOnly(false);
						}}
					/>
					{STAGES.map((s) => (
						<StatTile
							key={s.key}
							label={s.label}
							value={stats.byStage?.[s.key] ?? 0}
							active={stageFilter === s.key && !dueOnly}
							onClick={() => {
								setDueOnly(false);
								setStageFilter((cur) => (cur === s.key ? '' : s.key));
							}}
						/>
					))}
					<StatTile
						label='Follow-up due'
						value={stats.dueFollowUps}
						accent={stats.dueFollowUps > 0}
						active={dueOnly}
						onClick={() => {
							setStageFilter('');
							setDueOnly((v) => !v);
						}}
					/>
					<StatTile label='Pipeline $' value={fmtMoney(stats.totalAmount)} />
				</div>
			)}

			{/* Search */}
			<div className='mt-6 max-w-md'>
				<div className='relative'>
					<MagnifyingGlassIcon className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
					<input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder='Search name, email, phone…'
						className='block w-full rounded-md border-0 bg-white/5 py-2 pl-9 pr-3 text-sm text-white ring-1 ring-inset ring-white/10 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-500'
					/>
				</div>
			</div>

			{/* Table */}
			<div className='mt-6 overflow-hidden rounded-lg ring-1 ring-white/10'>
				{loading ? (
					<div className='py-16'>
						<Spinner color='white' />
					</div>
				) : leads.length === 0 ? (
					<p className='py-16 text-center text-sm text-gray-400'>
						No leads match these filters.
					</p>
				) : (
					<table className='min-w-full divide-y divide-white/10'>
						<thead className='bg-white/5'>
							<tr>
								{[
									'Name',
									'Contact',
									'Stage',
									'Forecast',
									'Amount',
									'Next step',
									'Follow-up',
								].map((h) => (
									<th
										key={h}
										className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400'
									>
										{h}
									</th>
								))}
							</tr>
						</thead>
						<tbody className='divide-y divide-white/5'>
							{leads.map((lead) => (
								<tr
									key={lead._id}
									onClick={() => setSelectedId(lead._id)}
									className='cursor-pointer hover:bg-white/5'
								>
									<td className='px-4 py-3 text-sm font-medium text-white'>
										{lead.name || '—'}
										{lead.hoa && (
											<span className='ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-gray-300'>
												HOA
											</span>
										)}
									</td>
									<td className='px-4 py-3 text-sm text-gray-300'>
										<div>{lead.email}</div>
										<div className='text-gray-500'>{lead.phone || ''}</div>
									</td>
									<td className='px-4 py-3'>
										<StagePill stage={lead.stage} />
									</td>
									<td className='px-4 py-3 text-sm capitalize text-gray-300'>
										{lead.forecastCategory || '—'}
									</td>
									<td className='px-4 py-3 text-sm text-gray-300'>
										{fmtMoney(lead.amount)}
									</td>
									<td className='max-w-[12rem] truncate px-4 py-3 text-sm text-gray-400'>
										{lead.nextStep || '—'}
									</td>
									<td className='px-4 py-3 text-sm'>
										<span
											className={
												isPast(lead.nextFollowUpAt)
													? 'font-medium text-rose-300'
													: 'text-gray-300'
											}
										>
											{lead.nextFollowUpAt ? fmtDate(lead.nextFollowUpAt) : '—'}
										</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{selectedId && (
				<LeadDrawer
					id={selectedId}
					onClose={() => setSelectedId(null)}
					onUpdated={(lead) => {
						replaceLead(lead);
						client.get('/crm/stats').then((r) => setStats(r.data)).catch(() => {});
					}}
				/>
			)}
		</div>
	);
}

function StatTile({ label, value, onClick, active, accent }) {
	const clickable = typeof onClick === 'function';
	return (
		<button
			type='button'
			onClick={onClick}
			disabled={!clickable}
			className={`rounded-lg px-3 py-3 text-left ring-1 transition-colors ${
				active
					? 'bg-primary-500/20 ring-primary-500/50'
					: clickable
					? 'bg-white/5 ring-white/10 hover:bg-white/10'
					: 'bg-white/5 ring-white/10 cursor-default'
			}`}
		>
			<div
				className={`text-xl font-semibold ${
					accent ? 'text-rose-300' : 'text-white'
				}`}
			>
				{value}
			</div>
			<div className='mt-0.5 truncate text-xs text-gray-400'>{label}</div>
		</button>
	);
}

function LeadDrawer({ id, onClose, onUpdated }) {
	const { setAlert } = useAlert();
	const [lead, setLead] = useState(null);
	const [loading, setLoading] = useState(true);
	const [noteBody, setNoteBody] = useState('');
	const [saving, setSaving] = useState(false);
	const [linkCopied, setLinkCopied] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		client
			.get(`/crm/leads/${id}`)
			.then((r) => {
				if (!cancelled) setLead(r.data.lead);
			})
			.catch((err) =>
				setAlert('error', err?.response?.data?.error || 'Failed to load lead')
			)
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [id, setAlert]);

	const patch = async (updates) => {
		setSaving(true);
		try {
			const { data } = await client.patch(`/crm/leads/${id}`, updates);
			setLead(data.lead);
			onUpdated(data.lead);
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Update failed');
		} finally {
			setSaving(false);
		}
	};

	const addNote = async () => {
		if (!noteBody.trim()) return;
		setSaving(true);
		try {
			const { data } = await client.post(`/crm/leads/${id}/notes`, {
				body: noteBody.trim(),
			});
			setLead(data.lead);
			onUpdated(data.lead);
			setNoteBody('');
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Could not add note');
		} finally {
			setSaving(false);
		}
	};

	const deleteNote = async (noteId) => {
		try {
			const { data } = await client.delete(`/crm/leads/${id}/notes/${noteId}`);
			setLead(data.lead);
			onUpdated(data.lead);
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Could not delete note');
		}
	};

	// --- Utility bill upload link + downloads ---
	const copyUploadLink = async () => {
		try {
			const { data } = await client.get(`/upload/link/${id}`);
			await navigator.clipboard.writeText(data.url);
			setLinkCopied(true);
			setTimeout(() => setLinkCopied(false), 2500);
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Could not create link');
		}
	};

	const downloadBill = async (bill) => {
		try {
			const res = await client.get(`/upload/bill/${id}/${bill._id}`, {
				responseType: 'blob',
			});
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const a = document.createElement('a');
			a.href = url;
			a.download = bill.originalName || 'utility-bill';
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			setAlert('error', 'Could not download file');
		}
	};

	const deleteBill = async (billId) => {
		try {
			const { data } = await client.delete(`/upload/bill/${id}/${billId}`);
			setLead(data.customer);
			onUpdated(data.customer);
		} catch (err) {
			setAlert('error', 'Could not delete file');
		}
	};

	const followUpInputValue = lead?.nextFollowUpAt
		? toLocalInput(lead.nextFollowUpAt)
		: '';

	const inputCls =
		'block w-full rounded-md border-0 bg-white/5 py-2 px-3 text-sm text-white ring-1 ring-inset ring-white/10 placeholder:text-gray-500 focus:ring-2 focus:ring-primary-500';

	return (
		<div className='fixed inset-0 z-50 flex justify-end'>
			<div
				className='absolute inset-0 bg-black/60'
				onClick={onClose}
				aria-hidden='true'
			/>
			<div className='relative flex h-full w-full max-w-lg flex-col overflow-y-auto bg-ink-900 shadow-xl ring-1 ring-white/10'>
				<div className='flex items-center justify-between border-b border-white/10 px-6 py-4'>
					<h2 className='text-lg font-semibold text-white'>Lead</h2>
					<button
						onClick={onClose}
						className='rounded p-1 text-gray-400 hover:bg-white/10 hover:text-white'
					>
						<XMarkIcon className='h-5 w-5' />
					</button>
				</div>

				{loading || !lead ? (
					<div className='py-20'>
						<Spinner color='white' />
					</div>
				) : (
					<div className='flex-1 space-y-8 px-6 py-6'>
						{/* Contact */}
						<div>
							<h3 className='text-xl font-semibold text-white'>
								{lead.name || 'Unnamed lead'}
							</h3>
							<dl className='mt-3 space-y-1.5 text-sm'>
								<Row label='Email'>
									<a
										href={`mailto:${lead.email}`}
										className='text-primary-400 hover:underline'
									>
										{lead.email}
									</a>
								</Row>
								<Row label='Phone'>
									{lead.phone ? (
										<a
											href={`tel:${lead.phone}`}
											className='text-primary-400 hover:underline'
										>
											{lead.phone}
										</a>
									) : (
										'—'
									)}
								</Row>
								<Row label='Address'>{lead.address || '—'}</Row>
								<Row label='Added'>{fmtDate(lead.createdAt)}</Row>
							</dl>
							<Link
								to={`/admin/proposals/new?customer=${lead._id}`}
								className='mt-4 inline-flex items-center gap-2 rounded-md bg-white/5 px-4 py-2 text-sm font-medium text-white ring-1 ring-white/10 hover:bg-white/10'
							>
								<DocumentTextIcon className='h-4 w-4 text-primary-400' />
								Build a proposal
							</Link>
						</div>

						{/* Pipeline */}
						<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
							<Field label='Stage'>
								<select
									value={lead.stage || ''}
									disabled={saving}
									onChange={(e) => patch({ stage: e.target.value })}
									className={inputCls}
								>
									{STAGES.map((s) => (
										<option key={s.key} value={s.key} className='bg-ink-900'>
											{s.label}
										</option>
									))}
								</select>
							</Field>
							<Field label='Forecast category'>
								<select
									value={lead.forecastCategory || ''}
									disabled={saving}
									onChange={(e) => patch({ forecastCategory: e.target.value })}
									className={inputCls}
								>
									{FORECAST_CATEGORIES.map((f) => (
										<option key={f.key} value={f.key} className='bg-ink-900'>
											{f.label}
										</option>
									))}
								</select>
							</Field>
							<Field label='Amount ($)'>
								<input
									type='number'
									min='0'
									step='100'
									defaultValue={lead.amount ?? ''}
									disabled={saving}
									onBlur={(e) => {
										const v = e.target.value;
										if (String(lead.amount ?? '') !== v) patch({ amount: v });
									}}
									placeholder='0'
									className={inputCls}
								/>
							</Field>
							<Field label='Owner'>
								<input
									defaultValue={lead.owner || ''}
									disabled={saving}
									onBlur={(e) => {
										if ((e.target.value || '') !== (lead.owner || ''))
											patch({ owner: e.target.value });
									}}
									placeholder='Who owns this lead?'
									className={inputCls}
								/>
							</Field>
							<Field label='Next step'>
								<input
									defaultValue={lead.nextStep || ''}
									disabled={saving}
									onBlur={(e) => {
										if ((e.target.value || '') !== (lead.nextStep || ''))
											patch({ nextStep: e.target.value });
									}}
									placeholder='e.g. Send proposal'
									className={inputCls}
								/>
							</Field>
							<Field label='Follow-up'>
								<input
									type='datetime-local'
									value={followUpInputValue}
									disabled={saving}
									onChange={(e) =>
										patch({
											nextFollowUpAt: e.target.value
												? new Date(e.target.value).toISOString()
												: null,
										})
									}
									className={inputCls}
								/>
								{isPast(lead.nextFollowUpAt) && (
									<p className='mt-1 flex items-center gap-1 text-xs text-rose-300'>
										<BellAlertIcon className='h-3.5 w-3.5' /> Follow-up is due
									</p>
								)}
							</Field>
						</div>

						<Field label='Next step note'>
							<textarea
								defaultValue={lead.nextStepNote || ''}
								disabled={saving}
								rows={2}
								onBlur={(e) => {
									if ((e.target.value || '') !== (lead.nextStepNote || ''))
										patch({ nextStepNote: e.target.value });
								}}
								placeholder='Detail on the next step…'
								className={inputCls}
							/>
						</Field>

						{/* HOA */}
						<div className='rounded-md bg-white/5 p-4 ring-1 ring-white/10'>
							<label className='flex items-center gap-3 text-sm text-gray-200'>
								<input
									type='checkbox'
									checked={!!lead.hoa}
									disabled={saving}
									onChange={(e) => patch({ hoa: e.target.checked })}
									className='h-4 w-4 rounded border-white/20 bg-white/5 text-primary-500 focus:ring-primary-500'
								/>
								HOA
							</label>
							{lead.hoa && (
								<div className='mt-3'>
									<Field label='HOA notes'>
										<textarea
											defaultValue={lead.hoaNotes || ''}
											disabled={saving}
											rows={2}
											onBlur={(e) => {
												if ((e.target.value || '') !== (lead.hoaNotes || ''))
													patch({ hoaNotes: e.target.value });
											}}
											placeholder='HOA approval details, contacts, restrictions…'
											className={inputCls}
										/>
									</Field>
								</div>
							)}
						</div>

						{/* Free-text notes */}
						<Field label='Notes'>
							<textarea
								defaultValue={lead.notesText || ''}
								disabled={saving}
								rows={3}
								onBlur={(e) => {
									if ((e.target.value || '') !== (lead.notesText || ''))
										patch({ notesText: e.target.value });
								}}
								placeholder='General notes about this lead…'
								className={inputCls}
							/>
						</Field>

						<Field label='Tags (comma-separated)'>
							<input
								defaultValue={(lead.tags || []).join(', ')}
								disabled={saving}
								onBlur={(e) => {
									const tags = e.target.value
										.split(',')
										.map((t) => t.trim())
										.filter(Boolean);
									if (tags.join(',') !== (lead.tags || []).join(','))
										patch({ tags });
								}}
								placeholder='e.g. md, residential'
								className={inputCls}
							/>
						</Field>

						{/* Utility bills */}
						<div>
							<div className='flex items-center justify-between'>
								<h4 className='text-sm font-semibold text-white'>Utility bills</h4>
								<button
									onClick={copyUploadLink}
									className='inline-flex items-center gap-1.5 rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-gray-200 ring-1 ring-white/10 hover:bg-white/10'
								>
									<LinkIcon className='h-3.5 w-3.5 text-primary-400' />
									{linkCopied ? 'Link copied!' : 'Copy upload link'}
								</button>
							</div>
							<p className='mt-1 text-xs text-gray-500'>
								Send the customer a link to upload their bill straight to this
								profile.
							</p>
							<ul className='mt-3 space-y-2'>
								{(lead.utilityBills || []).map((bill) => (
									<li
										key={bill._id}
										className='flex items-center gap-2 rounded-md bg-white/5 px-3 py-2 ring-1 ring-white/10'
									>
										<DocumentArrowDownIcon className='h-4 w-4 flex-none text-primary-400' />
										<button
											onClick={() => downloadBill(bill)}
											className='min-w-0 flex-1 truncate text-left text-sm text-gray-200 hover:text-white'
											title={bill.originalName}
										>
											{bill.originalName || 'bill'}
										</button>
										<span className='text-xs text-gray-500'>
											{bill.source === 'contact'
												? 'form'
												: bill.source === 'customer-link'
												? 'link'
												: 'admin'}
										</span>
										<button
											onClick={() => deleteBill(bill._id)}
											className='rounded p-1 text-gray-500 hover:text-rose-400'
											title='Delete'
										>
											<XMarkIcon className='h-4 w-4' />
										</button>
									</li>
								))}
								{(!lead.utilityBills || lead.utilityBills.length === 0) && (
									<li className='text-sm text-gray-500'>No bills uploaded yet.</li>
								)}
							</ul>
						</div>

						{/* Activity log */}
						<div>
							<h4 className='text-sm font-semibold text-white'>Activity log</h4>
							<div className='mt-3 flex gap-2'>
								<textarea
									value={noteBody}
									onChange={(e) => setNoteBody(e.target.value)}
									rows={2}
									placeholder='Add a dated note (call, email, what was discussed)…'
									className={inputCls}
								/>
								<button
									onClick={addNote}
									disabled={saving || !noteBody.trim()}
									className='shrink-0 self-start rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-40'
								>
									Add
								</button>
							</div>
							<ul className='mt-4 space-y-3'>
								{(lead.notes || [])
									.slice()
									.reverse()
									.map((note) => (
										<li
											key={note._id}
											className='group rounded-md bg-white/5 px-3 py-2 ring-1 ring-white/10'
										>
											<div className='flex items-start justify-between gap-2'>
												<p className='whitespace-pre-wrap text-sm text-gray-200'>
													{note.body}
												</p>
												<button
													onClick={() => deleteNote(note._id)}
													className='opacity-0 transition-opacity group-hover:opacity-100'
													title='Delete note'
												>
													<XMarkIcon className='h-4 w-4 text-gray-500 hover:text-rose-300' />
												</button>
											</div>
											<p className='mt-1 text-xs text-gray-500'>
												{note.author ? `${note.author} · ` : ''}
												{fmtDate(note.createdAt)}
											</p>
										</li>
									))}
								{(!lead.notes || lead.notes.length === 0) && (
									<li className='text-sm text-gray-500'>No notes yet.</li>
								)}
							</ul>
						</div>

						{/* Message history from the contact form */}
						{lead.messages?.length > 0 && (
							<div>
								<h4 className='text-sm font-semibold text-white'>
									Form submissions
								</h4>
								<ul className='mt-3 space-y-3'>
									{lead.messages.map((m) => (
										<li
											key={m._id}
											className='rounded-md bg-white/5 px-3 py-2 ring-1 ring-white/10'
										>
											<p className='whitespace-pre-wrap text-sm text-gray-200'>
												{m.message}
											</p>
											<p className='mt-1 text-xs text-gray-500'>
												{fmtDate(m.createdAt)}
											</p>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

function Row({ label, children }) {
	return (
		<div className='flex gap-2'>
			<dt className='w-20 shrink-0 text-gray-500'>{label}</dt>
			<dd className='text-gray-200'>{children}</dd>
		</div>
	);
}

function Field({ label, children }) {
	return (
		<div>
			<label className='mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400'>
				{label}
			</label>
			{children}
		</div>
	);
}

// ISO/Date -> 'YYYY-MM-DDTHH:mm' in the browser's local time, for datetime-local.
function toLocalInput(d) {
	const date = new Date(d);
	const pad = (n) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
		date.getDate()
	)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
